import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';

/**
 * Ship decision dialog — shown when a project finishes its debug phase.
 * Player chooses: Ship Now, Ship Early (with bugs), or Debug More.
 */
export function openShipProjectDialog(
  parent: HTMLElement,
  store: GameStore,
  projectId: string,
  onClose: () => void,
): void {
  const state = store.getState();
  const project = state.projects.find((p) => p.id === projectId);
  if (!project) return;

  const prevSpeed = state.speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: `Ship "${project.name}"?`,
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const content = document.createElement('div');
  content.style.cssText = 'display:flex;flex-direction:column;gap:12px;min-width:320px;';

  // Quality display
  const qualColor = project.quality >= 70 ? 'var(--color-success)' : project.quality >= 40 ? 'var(--color-warning)' : 'var(--color-danger)';
  const qualEl = document.createElement('div');
  qualEl.style.cssText = 'text-align:center;';
  qualEl.innerHTML = `
    <div style="font-size:24px;font-weight:700;color:${qualColor};">${Math.round(project.quality)}% Quality</div>
    <div style="font-size:14px;color:${project.bugs > 5 ? 'var(--color-danger)' : 'var(--color-text-muted)'};">${Math.round(project.bugs)} Bugs</div>
  `;
  content.appendChild(qualEl);

  // Bug impact explanation
  const bugImpact = Math.min(project.bugs * 0.5, 30);
  const finalQuality = Math.max(0, Math.round(project.quality - bugImpact));
  const infoEl = document.createElement('div');
  infoEl.style.cssText = 'font-size:11px;color:var(--color-text-secondary);text-align:center;border:1px solid var(--border-color);border-radius:4px;padding:8px;';
  infoEl.innerHTML = `
    Shipping with ${Math.round(project.bugs)} bugs will reduce quality by ${Math.round(bugImpact)}%.<br>
    <strong>Final quality if shipped now: <span style="color:${qualColor}">${finalQuality}%</span></strong>
  `;
  content.appendChild(infoEl);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;flex-direction:column;gap:6px;';

  // Ship Now
  const shipBtn = document.createElement('button');
  shipBtn.className = 'btn btn--primary';
  shipBtn.style.cssText = 'padding:10px;font-size:14px;';
  shipBtn.innerHTML = `<strong>Ship Now</strong><br><span style="font-size:10px;opacity:0.8;">Release with ${Math.round(project.bugs)} bugs · ${finalQuality}% quality</span>`;
  shipBtn.addEventListener('click', () => {
    store.dispatch({ type: 'SHIP_PROJECT', projectId });
    dialog.close();
  });
  btnRow.appendChild(shipBtn);

  // Debug More
  const debugBtn = document.createElement('button');
  debugBtn.className = 'btn';
  debugBtn.style.cssText = 'padding:10px;font-size:14px;';
  debugBtn.innerHTML = `<strong>Keep Debugging</strong><br><span style="font-size:10px;opacity:0.8;">Another debug cycle — may fix bugs, but risks adding more</span>`;
  debugBtn.addEventListener('click', () => {
    store.dispatch({ type: 'DEBUG_MORE', projectId });
    dialog.close();
  });
  btnRow.appendChild(debugBtn);

  // Ship Early hint
  if (project.bugs > 3) {
    const hintEl = document.createElement('div');
    hintEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);text-align:center;';
    hintEl.textContent = 'Tip: Skilled debuggers fix more bugs per cycle. Speed Demon trait adds bugs, Perfectionist trait reduces them.';
    btnRow.appendChild(hintEl);
  }

  content.appendChild(btnRow);
  dialog.body.appendChild(content);
  dialog.mount(parent);
}
